import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Form, Input, Select, Row, Col, Alert } from "antd";
import Spin from "Components/Spinner";
import { action_validate_cofidispay_identification_post } from "api/actions";
import SelectPaymentMethodModal from "../../../Shared/SelectPaymentMethodModal";
import { payment_cofidispay_post } from "api/payments";
import CustomButton from "Components/CustomButton";
import { DoubleRightOutlined, ReloadOutlined } from "@ant-design/icons";
import { messageErrorCode } from "./helper";
import scanningImage from "../../../../assets/images/scanning.gif";

import { order_post } from "api/orders";
import { utmParams } from "utils/helper";

const { utm_source, utm_medium, utm_campaign, utm_term, utm_content } = utmParams();
const env = process.env?.ENVIRONMENT?.toLowerCase() || "development";

const { Option } = Select;
const cofidisPayRequiredFields = {
  name: true,
  email: true,
  gender: true,
  phoneNumber: true,
};

const CofidisPay = ({ productData, data, actionHandler, step, customStyle, setShowChangeMethod }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const requiredText = t("cofidispay.required");
  const [isLoading, setLoading] = useState(false);
  const [selectPaymentMethod, setSelectPaymentMethod] = useState({ visible: false });
  const [genderLoading, setGenderLoading] = useState(false);
  const [errorFetching, setErrorFetching] = useState({ status: false, data: null });
  const [waitForPayment, setWaitForPayment] = useState(false);
  const [showCofidisPayModal, setShowCofidisPayModal] = useState({ visible: false, url: null });

  const [formValues, setFormValues] = useState(null);

  useEffect(() => {
    form.setFieldsValue({
      name: data?.identification?.name,
      email: data?.identification?.email,
      phoneNumber: data?.identification?.phone?.replace("+351", ""),
    });

    if (data?.identification?.name) {
      validateCofidisPayIdentification(data?.identification);
    }
  }, []);

  useEffect(() => {
    // Only show the "change method" link while the identification form is visible (not loading / error / redirecting).
    const show = errorFetching.status === false && !isLoading && !waitForPayment;
    if (setShowChangeMethod) setShowChangeMethod(show);
    return () => {
      if (setShowChangeMethod) setShowChangeMethod(false);
    };
  }, [errorFetching.status, isLoading, waitForPayment]);

  const onFinish = (values) => {
    setFormValues(values);
    startCofidisPayTransaction(values);
  };

  const startCofidisPayTransaction = async (values) => {
    if (!values) {
      values = formValues;
    }

    if (data?.identification?.email === undefined || data?.identification?.email === null) {
      return;
    }
    setLoading(true);

    const payload = {
      buyer: data.identification,
      products: data.purchase_products,
      purchase_id: data.purchase_id,
      main_product: data.main_product,
      coupon: data.coupon,
      cofidispay_data: values,
      pathname: window.location.pathname,
      origin: window.location.origin,
      affiliate_id: data.affiliate_id,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      free_resolution_waiver: data.identification?.free_resolution_checkbox || false,
      is_offer: data.identification?.is_offer || false,
      offer_info: {
        gift_name: data.identification?.gift_name || "",
        gift_email: data.identification?.gift_email || "",
        gift_message: data.identification?.gift_message || "",
        gift_delivery_date: data.identification?.gift_delivery_date || "",
      },
    };

    // Disable hability to change quantity
    actionHandler("qty_locked", true);

    const order = await createOrder({ ...payload, method: "cofidispay", source: "checkout" });
    if (!order.status) {
      setLoading(false);
      setErrorFetching({ status: true, data: "Erro ao criar pedido." });
      return;
    }

    const cofidisPayResponse = await dbPostCofidisPay(payload);
    if (!cofidisPayResponse.status) {
      setLoading(false);
      console.log("Error fetching Cofidis Pay response:", cofidisPayResponse.data?.response?.data?.code);
      setErrorFetching({ status: true, data: cofidisPayResponse.data?.response?.data?.code });
      return;
    }
    const cofidisPay = cofidisPayResponse.data;
    //console.log("cofidisPay", cofidisPay);

    if (cofidisPay.purchase_id !== payload.purchase_id || !cofidisPay.paymentURL) {
      setLoading(false);
      setErrorFetching({ status: true, data: "Erro ao iniciar pagamento." });
      return;
    }

    setWaitForPayment(true);
    setErrorFetching({ status: false });

    actionHandler("update_status", {
      action: "wait-for-payment",
      data: {
        method_id: "COFIDISPAY",
        entity: cofidisPay.entity,
        reference: cofidisPay.requestId,
        purchase_id: cofidisPay.purchase_id,
        payment_unique_id: cofidisPay.payment_unique_id,
      },
    });

    setLoading(false);

    setTimeout(() => {
      //console.log("cofidisPay", cofidisPay.paymentURL);

      // Redirect to CofidisPay.paymentURL in

      window.location.href = cofidisPay.paymentURL;
    }, 3000);
  };

  const validateCofidisPayIdentification = async (values) => {
    setGenderLoading(true);

    const timeoutPromise = new Promise((resolve) => {
      setTimeout(() => {
        resolve({ status: false, timeout: true });
      }, 4000); // 4 seconds timeout
    });

    const apiCallPromise = dbPostValidateCofidisPayIdentification(values);

    const response = await Promise.race([apiCallPromise, timeoutPromise]);

    if (response.status) {
      const gender = response?.data?.gender;
      if (gender === "MASCULINO" || gender === "FEMININO") {
        form.setFieldsValue({
          gender: gender === "MASCULINO" ? "M" : "F",
        });
      } else {
        form.setFieldsValue({ gender: null });
      }
    } else {
      // Timeout ou erro
      form.setFieldsValue({ gender: null });
    }

    setGenderLoading(false);
  };

  const dbPostValidateCofidisPayIdentification = async (values) => {
    return new Promise((resolve) => {
      try {
        action_validate_cofidispay_identification_post(values)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));

        // Error handling
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  const dbPostCofidisPay = async (values) => {
    return new Promise((resolve) => {
      try {
        payment_cofidispay_post(values)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));

        // Error handling
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  const createOrder = (payload) => {
    return new Promise((resolve) => {
      try {
        order_post(payload)
          .then((res) => resolve({ status: true, data: res }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  const ErrorCard = ({ code }) => {
    console.log("ErrorCard code", code);
    let message = t("cofidispay.errorGeneral");
    if (!code) message = <Trans i18nKey="cofidispay.errorUnavailable" components={{ strong: <strong />, br: <br /> }} />;

    if (Number.isInteger(code)) {
      message = messageErrorCode(code);
    }

    return (
      <div className="error">
        <Alert message={t("cofidispay.errorAlertTitle")} description={message} type="error" showIcon />
        <div style={{ textAlign: "center", marginTop: "25px", marginBottom: "25px" }}>
          <CustomButton buttonStyle={customStyle} type="outline" onClick={() => startCofidisPayTransaction()}>
            {t("cofidispay.retryButton")} <ReloadOutlined />
          </CustomButton>
        </div>
        <div style={{ textAlign: "center" }}>
          <CustomButton size="small" buttonStyle={customStyle} onClick={() => setSelectPaymentMethod({ visible: true })}>
            {t("cofidispay.changeMethodButton")}
          </CustomButton>
        </div>
      </div>
    );
  };

  return (
    <Spin spinning={isLoading} tip={t("cofidispay.loadingTip")}>
      <div className={"cofidispay-container"}>
        {isLoading && <div className="loading h-"></div>}
        {errorFetching.status === true && !isLoading && <ErrorCard code={errorFetching.data} />}

        {errorFetching.status === false && !isLoading && !waitForPayment && (
          <Form name="cofidispay" form={form} onFinish={onFinish} layout="vertical">
            <Form.Item
              // onBlur={() => validateCofidisPayIdentification(form.getFieldsValue())}
              label={t("cofidispay.nameLabel")}
              name="name"
              rules={[{ required: cofidisPayRequiredFields["name"], message: requiredText }]}
            >
              <Input />
            </Form.Item>
            <Form.Item label={t("cofidispay.emailLabel")} name="email" rules={[{ required: cofidisPayRequiredFields["email"], message: requiredText }]}>
              <Input type="email" />
            </Form.Item>

            <Row gutter={20}>
              <Col md={12} sm={24}>
                <Form.Item label={t("cofidispay.genderLabel")} name="gender" rules={[{ required: cofidisPayRequiredFields["gender"], message: requiredText }]}>
                  <Select style={{ width: "100%" }} disabled={genderLoading}>
                    <Option value="M">{t("cofidispay.genderMale")}</Option>
                    <Option value="F">{t("cofidispay.genderFemale")}</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col md={12} sm={24}>
                <Form.Item label={t("cofidispay.phoneLabel")} name="phoneNumber" rules={[{ required: cofidisPayRequiredFields["phoneNumber"], message: requiredText }]}>
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <CustomButton
              block
              buttonStyle={customStyle}
              // className="submit-button"
              disabled={genderLoading}
              htmlType="submit"
              type="primary"
            >
              {t("cofidispay.submitButton")} <DoubleRightOutlined />
              {/* <span id="button-text">
            {genderLoading ? <div className="spinner" id="spinner"></div> : "Continuar com Cofidis Pay"}
          </span> */}
            </CustomButton>
            <div style={{ marginTop: "20px" }}>
              <span style={{ fontSize: "12px", color: "#464646" }}>
                {t("cofidispay.disclaimer")}
              </span>
            </div>
          </Form>
        )}

        {waitForPayment && errorFetching.status === false && (
          <div>
            <div className="payment-loading-title">{t("cofidispay.waitingTitle")}</div>
            <div className="payment-loading-sub-title">{t("cofidispay.waitingSubtitle")}</div>
            <div style={{ display: "flex", justifyContent: "space-evenly", alignItems: "center" }}>
              <div className="payment-loading-image">
                <img width={50} src={scanningImage} alt={t("cofidispay.waitingAlt")} />
              </div>
            </div>
          </div>
        )}
      </div>

      <SelectPaymentMethodModal
        data={data}
        actionHandler={actionHandler}
        visible={selectPaymentMethod.visible}
        close={() => setSelectPaymentMethod({ visible: false })}
        customStyle={customStyle}
      />
    </Spin>
  );
};

export default CofidisPay;
